import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { useRef } from "react";
import { GlobalContext } from "../context/GlobalContext";
import LogoSvg from "../assets/sidebar/logo7G Secondary.svg";
import Profile from "./Common/Profile";
import "../styles/common.css";
import {
  resolveActiveRoute,
  resolveDefaultRoute,
  resolveFirstEnabledSubRoute,
  SIDEBAR_CONFIG,
  SIDEBAR_SUBNAV_CONFIG,
  SUBNAV_ICON_MAP,
} from "./SideBar/SideBarConfig";
import { getUserAttributes } from "../utils/auth";

const useStyles = makeStyles((theme: Theme) => ({
  createTaskWrapper: {
    marginTop: "54px",
    marginBottom: "11px",
    [theme.breakpoints.down("lg")]: {
      marginTop: "38px",
      marginBottom: "8px",
    },
  },
  createTask: {
    width: "51px",
    height: "52.351px",
    [theme.breakpoints.down("lg")]: {
      width: "41px",
      height: "42.351px",
    },
  },
  routeWrapper: {
    width: "48px",
    height: "48px",
    padding: "11px",
    [theme.breakpoints.down("lg")]: {
      width: "48px",
      height: "48px",
      padding: "8px",
    },
  },
  routeImage: {
    width: "29px",
    height: "29px",
    [theme.breakpoints.down("lg")]: {
      width: "25px",
      height: "25px",
    },
  },
  userAvatar: {
    width: "32px",
    height: "32px",
    [theme.breakpoints.down("lg")]: {
      width: "32px",
      height: "32px",
    },
    cursor: "pointer",
    marginLeft: "0px",
    marginTop: "5px",
    borderRadius: "50%",
    transition: "box-shadow 0.3s ease-in-out",
    "&:hover": {
      boxShadow: "0 0 0 4px rgba(234, 201, 201, 0.1)", // subtle glow ring
    },
  },
}));

type SidebarProps = {
  setSwitchState: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSpinner?: (state: boolean) => void;
};

const Sidebar = ({ setShowSpinner }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeRoute, setActiveRoute] = useState("/client");
  const classes = useStyles();
  const { userTeamData, featureMap, featureStatus, accountDetails, impersonationDetails } = useContext(GlobalContext);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const hasNavigatedOnce = useRef(false);
  const tryNavigateWithGuard = (
    route: string,
    options?: { replace?: boolean }
  ) => {
    const guard = (window as any).__unsavedNavigationGuard as
      | ((nextRoute: string, proceed: () => void) => boolean | void)
      | undefined;

    const proceed = () => {
      setActiveRoute(route);
      navigate(route, options);
    };

    if (guard) {
      const handled = guard(route, proceed);
      if (handled === false || handled === true) return;
    }

    proceed();
  };
  const navigateWithBackground = (route: string) => {
    tryNavigateWithGuard(route);
  };
  const [disableRoutes, setDisableRoutes] = useState<boolean>(false);
  const isExternalUser = accountDetails?.isExternalUser;
  const isMeetingIdPresent = matchPath("/meeting/:id", location.pathname);
  const userInfo = getUserAttributes();
  const loggedInEmail = userInfo.email;
  const isHighRadiusUser = loggedInEmail?.endsWith("@highradius.com");
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const isTeamAccessLevel = impersonationDetails?.accessType === "team";

  const primaryItems =
    SIDEBAR_CONFIG.find((g) => g.group === "primary")?.items.filter(
      (item) => featureMap[item.feature]
    ) ?? [];

  const secondaryItems =
    SIDEBAR_CONFIG.find((g) => g.group === "secondary")?.items.filter(
      (item) => featureMap[item.feature]
    ) ?? [];

  const renderSubMenuFromConfig = (key: keyof typeof SIDEBAR_SUBNAV_CONFIG) => {
    const section = SIDEBAR_SUBNAV_CONFIG[key];

    if (!featureMap[section.feature]) return null;

    const visibleItems = section.items.filter((item) => {
      // Team tabs (team_interactions, team_tasks, team_analytics) depend ONLY on showTeamTab
      // They should NOT depend on feature flags
      if (item.feature.startsWith("team_")) {
        if (isImpersonating) {
          return isTeamAccessLevel && userTeamData?.showTeamTab == true;
        }
        return userTeamData?.showTeamTab === true;
      }

      // For non-team items, check feature flag
      if (!featureMap[item.feature]) return false;

      return true;
    });

    if (!visibleItems.length) return null;

    return (
      <Box className="submenu-tooltip">
        <Box className="submenu-title">{section.title}</Box>

        {visibleItems.map((item) => (
          <Box
            key={item.label}
            className="submenu-item"
            onClick={(e) => {
              e.stopPropagation();
              // Close the parent tooltip when clicking submenu item
              setHoveredMenu(null);
              navigateWithBackground(item.route);
            }}
          >
            <img
              src={SUBNAV_ICON_MAP[item.icon]}
              alt={item.label}
              className="submenu-icon"
            />
            <h1 className="submenu-label">{item.label}</h1>
          </Box>
        ))}
      </Box>
    );
  };
  useEffect(() => {
    setDisableRoutes(false);

    // Wait for feature flags to be ready before navigation to prevent race conditions
    if (featureStatus !== "ready" && featureStatus !== "error") {
      return;
    }

    const isRootRoute =
      location.pathname === "/" || location.pathname === "/home";

    // 1 First navigation (default landing)
    if (!hasNavigatedOnce.current && isRootRoute) {
      const defaultRoute = resolveDefaultRoute(featureMap, isHighRadiusUser);
      setActiveRoute(defaultRoute);
      navigate(defaultRoute, { replace: true });
      hasNavigatedOnce.current = true;
      return;
    }
    if (isExternalUser && !isMeetingIdPresent) {
      // Check if shared_with_me_interactions feature is enabled before redirecting
      // Only redirect if feature is explicitly enabled (true) or not defined (backward compatibility)
      if (featureMap["shared_with_me_interactions"] !== false) {
        setActiveRoute("/meetings/shared-with-me");
        navigate("/meetings/shared-with-me", { replace: true });
      } else {
        // If feature is disabled, redirect to default route
        const defaultRoute = resolveDefaultRoute(featureMap, isHighRadiusUser);
        setActiveRoute(defaultRoute);
        navigate(defaultRoute, { replace: true });
      }
      return;
    }
    const resolvedRoute = resolveActiveRoute(location.pathname);
    setActiveRoute(resolvedRoute);
  }, [
    location.pathname,
    featureMap,
    featureStatus,
    navigate,
    isExternalUser,
    isMeetingIdPresent,
  ]);

  useEffect(() => {
    // Ensure controlled tooltip state is reset after every page transition.
    setHoveredMenu(null);
  }, [location.pathname]);

  const MenuItemComponent: React.FC<{
    icon: any;
    brandIcon: any;
    text: string;
    route?: string;
    label?: string;
    onClick?: () => void;
    disableRoutes?: boolean;
    onMouseEnter?: React.MouseEventHandler<Element>;
    onMouseLeave?: React.MouseEventHandler<Element>;
  }> = ({ icon, brandIcon, label, text, route, onClick, disableRoutes }) => {
    const sectionRoute = route ? `/${route.split("/")[1]}` : "";
    const isActive = Boolean(
      route &&
        (activeRoute === route ||
          (sectionRoute && activeRoute?.startsWith(`${sectionRoute}/`)))
    );

    disableRoutes = disableRoutes && route !== "/client";
    const tooltipNode = (
      <Box className="tooltip-node">
        <h1 className="tooltip-node-title">{text}</h1>
      </Box>
    );
    const offset = [0, 0];
    const tooltipPlacement = [
      "Accounts",
      "Interactions",
      "Tasks",
      "Analytics",
    ].includes(text)
      ? "right-start"
      : "right";
    return (
      <Box
        key={text}
        onMouseEnter={() => setHoveredMenu(text)}
        onMouseLeave={() => setHoveredMenu(null)}
        onClick={
          disableRoutes
            ? undefined
            : route
              ? () => {
                const resolvedRoute = resolveFirstEnabledSubRoute(
                  route,
                  featureMap,
                  isHighRadiusUser
                );
                // Close tooltip when clicking on menu item
                setHoveredMenu(null);
                navigateWithBackground(resolvedRoute);
              }
              : onClick
        }
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          alignSelf: "center",
          cursor: disableRoutes ? "not-allowed" : "pointer",
          padding: "8px 12px", // Add uniform padding
          justifyContent: "flex-start",
          width: "100%",
          opacity: disableRoutes ? 0.5 : 1, // Gray out disabled items
          transition: "background-color 0.3s ease", // Smooth transition
        }}
      >
        <Tooltip
       open={hoveredMenu === text}
       onClose={() => setHoveredMenu(null)}
       disableHoverListener
       disableFocusListener
       disableTouchListener
          className="sidebar-custom-tooltip"
          title={
            text === "Accounts"
              ? renderSubMenuFromConfig("accounts")
              : text === "Interactions"
                ? renderSubMenuFromConfig("interactions")
                : text === "Tasks"
                  ? renderSubMenuFromConfig("tasks")
                  : text === "Analytics"
                    ? renderSubMenuFromConfig("analytics")
                    : text === "Settings"
                      ? renderSubMenuFromConfig("settings")
                      : tooltipNode
          }
          placement={tooltipPlacement}
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: { offset },
              },
            ],
          }}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#FFFFFF",
                padding: 0,
                "& .MuiTooltip-arrow": {
                  color: "#FFFFFF", // 👈 arrow matches tooltip background
                },
              },
            },
          }}
          arrow
        >
          <Box
            className="innerBox"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              /* ACTIVE (SELECTED) STATE */
              backgroundColor: isActive
                ? "rgba(255, 255, 255, 0.40)"
                : "transparent",

              boxShadow: isActive ? "0 6px 14px rgba(127,1,139,0.25)" : "none",

              transition:
                "background-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",

              /* HOVER EFFECT — ONLY WHEN NOT ACTIVE */
              ...(hoveredMenu === text &&
              !disableRoutes &&
              !isActive && {
                backgroundColor: "rgba(255,255,255,0.12)",
                transform: "scale(1.08)",
                boxShadow: "0 6px 14px rgba(127,1,139,0.25)",
              }),

            /* ICON-HOVER EFFECT */
            "&:hover":
              disableRoutes || isActive
                ? {}
                : {
                    backgroundColor: "rgba(255,255,255,0.12)",
                    transform: "scale(1.08)",
                    boxShadow: "0 6px 14px rgba(127,1,139,0.25)",
                  },
                    }}
          >
            <img
              src={isActive ? brandIcon : icon}
              alt={text}
              className={classes.routeImage}
              style={{
                height: "20px",
                width: "40px",
                top: "14px",
                left: "16px",
              }}
              loading="eager"
            />
          </Box>
        </Tooltip>
        <Box
          sx={{
            fontSize: text === "Opportunities" || route === "/opportunities" ? "9.2px" : "10px",
            lineHeight: "12px",
            color: "#FFFFFF",
            textAlign: "center",
            maxWidth: "60px",
            whiteSpace: "wrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            opacity: disableRoutes ? 0.6 : 1,
            paddingTop: 0.5,
          }}
        >
          {label}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      className="sidebarWrapper"
      sx={{
        zIndex: 1,
        background: "linear-gradient(to bottom, #7F018B, #35013A)",
        float: "left",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "25px 10px",
        position: "relative",
        width: "60px",
        maxWidth: "86px",
        minWidth: "70px",
        overflow: "hidden",
      }}
    >
      <img
        src={LogoSvg}
        alt="7thGear"
        onClick={() => {
          // Close any open tooltip when clicking logo
          setHoveredMenu(null);
          if (isExternalUser) {
            tryNavigateWithGuard("/meetings/shared-with-me");
            return;
          }
        
          const defaultRoute = resolveDefaultRoute(
            featureMap,
            isHighRadiusUser
          );
        
          tryNavigateWithGuard(defaultRoute);
        }}
        style={{
          width: "30px",
          height: "48px",
          alignItems: "center",
          top: "12px",
          left: "15px",
          cursor: "pointer",
          paddingBottom: "18px",
        }}
      />
      <Box className="flex flex-col justify-between items-center sidebarContent">
        <Box
          className="flex-column-start"
          sx={{
            flex: "1",
            gap: "2px",
            alignItems: "center",
            top: "88px",
            left: "5px",
          }}
        >
          {primaryItems.map(({ text, icon, brandIcon, label, route }) => (
            <MenuItemComponent
              key={text}
              icon={icon}
              brandIcon={brandIcon}
              text={text}
              label={label}
              route={route}
              disableRoutes={disableRoutes}
            />
          ))}
        </Box>

        <Box
          className="flex-column-between"
          sx={{
            gap: "2px",
            alignItems: "center",
            bottom: "0",
            left: "5px",
            paddingBottom: "10px",
          }}
        >
          {secondaryItems.map(({ text, icon, brandIcon, label, route }) => (
            <MenuItemComponent
              key={text}
              icon={icon}
              brandIcon={brandIcon}
              text={text}
              label={label}
              route={route}
              disableRoutes={disableRoutes}
            />
          ))}
          <Box
            className="flex-start"
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <Box>
              <Profile
                classes={classes}
                setShowSpinner={(s: boolean) =>
                  setShowSpinner && setShowSpinner(s)
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
